import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, Button, Card, ConfirmDialog, Field, InfoTip, MonthPicker, PillButton, apiErrorMessage, formatMoney, brandLocalDate, rebaseDatePrefill, useBrand } from '@aralere/shared-ui';
import { QuickTimer, timerElapsedSeconds, isRunning } from '../../components/QuickTimer';
import type {
  Business,
  Client,
  CreateTimeEntryRequest,
  EntryMode,
  ListTimeEntriesResponse,
  ProjectWithTotals,
  RetainerMonthSummary,
  Service,
  TimeEntry,
  UpdateTimeEntryRequest,
} from '@aralere/shared-types';

// Time tracking admin (spec §10.4/§8.10; slice 3 s47, reworked slice 3.1 s48).
// s48 shape: one client in view, the WHOLE CURRENT YEAR loaded and grouped into month sections
// (newest first, headers like "July 2026", row dates MM-DD-YYYY); the month+year picker scrolls
// the chosen month to the top and drives which month the retainer cards derive for (switching
// year reloads). The retainer draw is CATEGORY-DRIVEN (Fork 1(i)): the Category picker's
// vocabulary is the union of the client's active retainers' workTypeTags plus "Other" — a category
// matching exactly one retainer auto-targets it (mirrored server-side); the Retainer select stays
// as the per-entry override; the linked service is reporting context only now. Required fields
// carry the red asterisk via the shared Field (the s48 platform standard). Mobile-first: entries
// render as stacked cards under md and as the table from md up. Billed entries stay locked.
// Projects picker still deferred (slice 6). s49 (slice 3.2): the live Start/End timer — the
// QuickTimer buttons sit under the header; open timers are DRAFT entries shown in the Running
// section (live elapsed; Stop-and-complete per row; EDITABLE while running per the owner's rule —
// the form gains a draft mode with "Save draft" [partial, stays draft] and "Complete entry" [full
// validation, charge/draw compute]); drafts are excluded from the month sections/totals; deep
// links ?client=&complete= (from the dashboard) auto-select the client and open the completion
// form. Row actions use the shared PillButton (the portal's row-action standard).

interface FormState {
  date: string;
  description: string;
  category: string; // '' = Other/uncategorized
  entryMode: EntryMode;
  hours: string;
  minutes: string;
  startAt: string; // datetime-local values
  endAt: string;
  hourlyRate: string;
  billable: boolean;
  cost: string;
  serviceId: string;
  projectId: string; // '' = no project (slice 6); else a proj- id of THIS client
  retainer: string; // '' = no retainer; else a clientServiceId
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];


/** YYYY-MM → "July 2026". */
function monthLabel(month: string): string {
  const m = parseInt(month.slice(5, 7), 10);
  return `${MONTH_NAMES[(m - 1 + 12) % 12] ?? month} ${month.slice(0, 4)}`;
}

/** YYYY-MM-DD → MM-DD-YYYY (the s48 display rule for entry dates). */
function displayDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${m}-${d}-${y}`;
}

/** datetime-local ('YYYY-MM-DDTHH:mm', local) → ISO; '' → null. */
function localToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** ISO → datetime-local value in the browser's zone; null → ''. */
function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// 04 #136: the entry date prefills to today on the brand clock of the client's brand (02 s177).
const emptyForm = (ratePrefill: number | null, timezone: string | null): FormState => ({
  date: brandLocalDate(timezone),
  description: '',
  category: '',
  entryMode: 'duration',
  hours: '',
  minutes: '',
  startAt: '',
  endAt: '',
  hourlyRate: ratePrefill != null ? String(ratePrefill) : '',
  billable: true,
  cost: '',
  serviceId: '',
  projectId: '',
  retainer: '',
});

function formFromEntry(e: TimeEntry): FormState {
  // A draft (open timer) completes in DURATION mode prefilled with its banked total (accumulated +
  // any running segment) — pause/resume means there's no single start/end to show (s50).
  if (e.draft === true) {
    const totalMin = Math.max(1, Math.round(timerElapsedSeconds(e, Date.now()) / 60));
    return {
      date: e.date,
      description: e.description,
      category: e.category ?? '',
      entryMode: 'duration',
      hours: String(Math.floor(totalMin / 60)),
      minutes: String(totalMin % 60),
      startAt: '',
      endAt: '',
      hourlyRate: String(e.hourlyRate),
      billable: e.billable,
      cost: e.cost ? String(e.cost) : '',
      serviceId: e.serviceId ?? '',
      projectId: e.projectId ?? '',
      retainer: e.retainerDrawId ?? '',
    };
  }
  const endForForm = e.endAt;
  return {
    date: e.date,
    description: e.description,
    category: e.category ?? '',
    entryMode: e.entryMode,
    hours: e.entryMode === 'duration' ? String(Math.floor(e.minutes / 60)) : '',
    minutes: e.entryMode === 'duration' ? String(e.minutes % 60) : '',
    startAt: isoToLocal(e.startAt),
    endAt: isoToLocal(endForForm),
    hourlyRate: String(e.hourlyRate),
    billable: e.billable,
    cost: e.cost ? String(e.cost) : '',
    serviceId: e.serviceId ?? '',
    projectId: e.projectId ?? '',
    retainer: e.retainerDrawId ?? '',
  };
}

function formMinutes(f: FormState): number | null {
  if (f.entryMode === 'duration') {
    const h = f.hours.trim() ? parseInt(f.hours, 10) : 0;
    const m = f.minutes.trim() ? parseInt(f.minutes, 10) : 0;
    if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || m < 0) return null;
    const total = h * 60 + m;
    return total >= 1 && total <= 1440 ? total : null;
  }
  const s = localToIso(f.startAt);
  const e = localToIso(f.endAt);
  if (!s || !e) return null;
  const mins = Math.round((Date.parse(e) - Date.parse(s)) / 60000);
  return mins >= 1 && mins <= 1440 ? mins : null;
}

const roundUp = (minutes: number, inc: number): number => Math.ceil(minutes / inc) * inc;
const fmtHours = (h: number): string => (Number.isInteger(h) ? String(h) : h.toFixed(2).replace(/0+$/, '').replace(/\.$/, ''));
const fmtMoney = formatMoney; // shared thousands-separated formatter (s58)
const slugTag = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, '-');

/** The category vocabulary: the union of the active retainers' workTypeTags (Fork 1(i)). */
function categoryOptions(retainers: RetainerMonthSummary[]): string[] {
  const seen = new Map<string, string>(); // slug → first-seen display form
  for (const r of retainers) {
    for (const t of r.workTypeTags) {
      const k = slugTag(t);
      if (k && !seen.has(k)) seen.set(k, t);
    }
  }
  return [...seen.values()];
}

/** Mirror of the server's s48 category-driven auto-select — preview only; the server is authoritative. */
function retainerForCategory(retainers: RetainerMonthSummary[], category: string): string {
  if (!category.trim()) return '';
  const key = slugTag(category);
  const hits = retainers.filter((r) => r.workTypeTags.some((t) => slugTag(t) === key));
  return hits.length === 1 ? hits[0].clientServiceId : '';
}

export function AdminTimeEntries(): JSX.Element {
  const { brand: portalBrand } = useBrand();
  const portalTz = portalBrand?.timezone ?? null;
  const [clients, setClients] = useState<Client[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [clientId, setClientId] = useState('');
  // 04 #136: the page's month and year default to THIS month on the portal brand's clock (a view
  // filter, not a record date); the year is derived from the same reading, so the two cannot disagree.
  const [year, setYear] = useState<string>(() => brandLocalDate(portalTz).slice(0, 4));
  const [month, setMonth] = useState<string>(() => brandLocalDate(portalTz).slice(0, 7));
  const [data, setData] = useState<ListTimeEntriesResponse | null>(null);
  /** s72 JANUARY GRACE: while it's January and the current year is loaded, last December's
   *  completed entries stay visible as an extra month section (fetched best-effort from the
   *  prior year). Totals (MTD/YTD) stay strictly the selected year's — the grace is display
   *  only; the prior year remains fully reachable through the month picker. */
  const [graceEntries, setGraceEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TimeEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const monthRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingScroll = useRef<string | null>(null);

  // s49: open timers (drafts) + dashboard deep links (?client=&complete=).
  const [openTimers, setOpenTimers] = useState<TimeEntry[]>([]); // ALL clients' open timers (s50)
  const [projects, setProjects] = useState<ProjectWithTotals[]>([]); // the selected client's projects (slice 6)
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingComplete = useRef<string | null>(searchParams.get('complete'));
  const [timerNow, setTimerNow] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [c, s, b] = await Promise.all([api.admin.listClients(), api.admin.listServices(), api.admin.listBusinesses()]);
        if (!alive) return;
        setClients(c.clients.map((r) => r.client));
        setServices(s.services);
        setBusinesses(b.businesses);
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const load = async (cid: string, m: string, y: string): Promise<void> => {
    if (!cid) return;
    setLoading(true);
    setError(null);
    try {
      setData(await api.admin.listTimeEntries(cid, m, y));
      const nowJan = new Date();
      if (nowJan.getMonth() === 0 && y === String(nowJan.getFullYear())) {
        try {
          const prev = await api.admin.listTimeEntries(cid, m, String(nowJan.getFullYear() - 1));
          setGraceEntries(prev.entries.filter((e) => e.date.slice(5, 7) === '12' && e.draft !== true));
        } catch {
          setGraceEntries([]); // grace is best-effort; the year view stands on its own
        }
      } else {
        setGraceEntries([]);
      }
      const openRes = await api.admin.listOpenTimers(); // all clients (s50 — the Time page shows every running timer)
      setOpenTimers(openRes.timers);
      // Slice 6: the client's projects feed the entry form's Project picker (best-effort — a
      // projects hiccup must not take down the Time page).
      try {
        const projRes = await api.admin.listProjects(cid);
        setProjects(projRes.projects);
      } catch {
        setProjects([]);
      }
    } catch (e) {
      setError(apiErrorMessage(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) void load(clientId, month, year);
    else setData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, month, year]);

  // 04 #136: when the portal brand arrives after mount, an untouched month default follows its clock.
  useEffect(() => {
    const next = rebaseDatePrefill(month, null, portalTz);
    if (next !== month) {
      setMonth(next);
      setYear(next.slice(0, 4));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalTz]);

  /** 04 #136: the timezone a NEW entry's date is taken in: the client's brand (its `timezone`,
   *  absent = the platform default), else the portal brand. */
  const clientTz = (cid: string): string | null => {
    const c = clients.find((x) => x.id === cid);
    const b = c ? businesses.find((x) => x.id === c.primaryBusinessId) : undefined;
    return b ? b.timezone ?? null : portalTz;
  };

  // Dashboard deep link: ?client= selects the client once.
  useEffect(() => {
    const c = searchParams.get('client');
    if (c && !clientId) setClientId(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dashboard deep link: ?complete=<entryId> opens the completion form once its draft is loaded.
  useEffect(() => {
    const target = pendingComplete.current;
    if (!target || !data) return;
    const draft = openTimers.find((t) => t.id === target);
    if (draft) {
      pendingComplete.current = null;
      setSearchParams({}, { replace: true });
      openEdit(draft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTimers, data]);

  // Invoice-line deep link (s77 #16b): ?focus=<entryId> scrolls to + highlights that time entry once
  // its month is loaded. The highlight persists (bg tint) so the entry stays findable after the scroll.
  const focusId = searchParams.get('focus');
  useEffect(() => {
    if (!focusId || loading) return;
    const el = document.getElementById(`src-${focusId}`) ?? document.getElementById(`src-m-${focusId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId, loading, data]);

  const refreshOpenTimers = async (): Promise<void> => {
    try {
      const res = await api.admin.listOpenTimers();
      setOpenTimers(res.timers);
    } catch {
      /* the Running section is a convenience; the buttons surface real errors */
    }
  };

  // Show running timers even before a client is picked (request 1).
  useEffect(() => {
    void refreshOpenTimers();
  }, []);

  // Live elapsed tick while any timer runs.
  useEffect(() => {
    if (!openTimers.some((t) => isRunning(t))) return;
    const id = window.setInterval(() => setTimerNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [openTimers]);

  // s52/s53: per-retainer overage (MTD) + the month's (MTD) and year's (YTD) totals, derived from
  // the loaded entries. `data.entries` is the whole loaded year, so YTD = all of them; MTD filters
  // to `data.month`. Entries carry the server's authoritative charge/drawn, so summing is accurate.
  // Open timers (drafts) are excluded.
  const monthStats = useMemo(() => {
    const overageByRetainer: Record<string, { hours: number; charge: number }> = {};
    let mtdMin = 0;
    let mtdBillableMin = 0;
    let mtdCharge = 0;
    let ytdMin = 0;
    let ytdBillableMin = 0;
    let ytdCharge = 0;
    if (data) {
      for (const e of data.entries) {
        if (e.draft === true) continue;
        ytdMin += e.minutes;
        if (e.billable) ytdBillableMin += e.minutes;
        ytdCharge += e.charge;
        if (e.date.startsWith(data.month)) {
          mtdMin += e.minutes;
          if (e.billable) mtdBillableMin += e.minutes;
          mtdCharge += e.charge;
          if (e.retainerDrawId) {
            const overageHrs = Math.max(0, e.minutes / 60 - (e.retainerHoursDrawn ?? 0));
            const cur = overageByRetainer[e.retainerDrawId] ?? { hours: 0, charge: 0 };
            cur.hours += overageHrs;
            cur.charge += e.charge;
            overageByRetainer[e.retainerDrawId] = cur;
          }
        }
      }
    }
    const bucket = (min: number, billMin: number, charge: number) => ({
      totalHours: min / 60,
      billableHours: billMin / 60,
      nonBillableHours: (min - billMin) / 60,
      totalCharge: Math.round(charge * 100) / 100,
    });
    return {
      mtd: bucket(mtdMin, mtdBillableMin, mtdCharge),
      ytd: bucket(ytdMin, ytdBillableMin, ytdCharge),
      overageByRetainer,
    };
  }, [data]);

  // The whole year grouped into month sections, newest first.
  const monthGroups = useMemo(() => {
    if (!data) return [] as { month: string; entries: TimeEntry[] }[];
    const by = new Map<string, TimeEntry[]>();
    for (const e of [...data.entries, ...graceEntries]) {
      if (e.draft === true) continue; // open timers live in the Running section, not the totals
      const k = e.date.slice(0, 7);
      const arr = by.get(k);
      if (arr) arr.push(e);
      else by.set(k, [e]);
    }
    return [...by.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([m, entries]) => ({ month: m, entries }));
  }, [data, graceEntries]);

  // After a load triggered by the month picker, scroll that month's section to the top.
  useEffect(() => {
    const target = pendingScroll.current;
    if (!target || loading) return;
    pendingScroll.current = null;
    const el = monthRefs.current[target];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [monthGroups, loading]);

  // s51: one combined month+year picker (native `type="month"`, value "YYYY-MM"). Changing the
  // month scrolls to it; changing the year reloads (the load effect keys on year). Keeping `year`
  // in sync drives that reload.
  const onPickMonth = (value: string): void => {
    if (!value) return;
    pendingScroll.current = value;
    setYear(value.slice(0, 4));
    setMonth(value);
  };

  const update = (patch: Partial<FormState>): void => setForm((f) => (f ? { ...f, ...patch } : f));

  const openCreate = (): void => {
    if (!data) return;
    const f = emptyForm(data.defaults.hourlyRatePrefill, clientTz(clientId));
    // A single active retainer pre-targets even before a category is chosen (the common case).
    if (data.retainers.length === 1) f.retainer = data.retainers[0].clientServiceId;
    setEditing(null);
    setForm(f);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (e: TimeEntry): void => {
    setEditing(e);
    setForm(formFromEntry(e));
    setFormError(null);
    setFormOpen(true);
  };

  const onCategoryPicked = (cat: string): void => {
    if (!form || !data) return;
    // The category drives the draw (s48): re-target the retainer preview; the select stays an override.
    const auto = retainerForCategory(data.retainers, cat);
    update({ category: cat, retainer: cat ? auto : data.retainers.length === 1 ? data.retainers[0].clientServiceId : '' });
  };

  // s49: a partial draft save — stays a draft; no full validation (the editable-while-running rule).
  const doSaveDraft = async (): Promise<void> => {
    if (!form || !editing || !clientId) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.admin.updateTimeEntry(editing.id, clientId, {
        description: form.description,
        category: form.category.trim() ? form.category.trim() : null,
        date: form.date || undefined,
        startAt: form.startAt ? localToIso(form.startAt) : undefined,
        endAt: form.endAt ? localToIso(form.endAt) : undefined,
        hourlyRate: form.hourlyRate.trim() && Number.isFinite(parseFloat(form.hourlyRate)) ? Math.round(parseFloat(form.hourlyRate) * 100) / 100 : undefined,
        billable: form.billable,
        serviceId: form.serviceId || null,
        projectId: form.projectId || null,
      });
      setFormOpen(false);
      setForm(null);
      setEditing(null);
      await refreshOpenTimers();
      await load(clientId, month, year);
    } catch (e) {
      setFormError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const doSave = async (): Promise<void> => {
    if (!form || !data || !clientId) return;
    const mins = formMinutes(form);
    if (!mins) {
      setFormError(form.entryMode === 'duration' ? 'Enter a duration between 1 minute and 24 hours.' : 'Enter a start and end time (up to 24 hours apart).');
      return;
    }
    if (!form.description.trim()) {
      setFormError('A description is required.');
      return;
    }
    const rate = parseFloat(form.hourlyRate);
    if (!form.hourlyRate.trim() || !Number.isFinite(rate) || rate < 0) {
      setFormError('Enter a non-negative hourly rate.');
      return;
    }
    const cost = form.cost.trim() ? parseFloat(form.cost) : 0;
    if (!Number.isFinite(cost) || cost < 0) {
      setFormError('Cost must be a non-negative amount (or blank for 0).');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const common = {
        date: form.date,
        description: form.description.trim(),
        category: form.category.trim() ? form.category.trim() : null,
        entryMode: form.entryMode,
        hourlyRate: Math.round(rate * 100) / 100,
        billable: form.billable,
        cost: Math.round(cost * 100) / 100,
        serviceId: form.serviceId || null,
        projectId: form.projectId || null,
        retainerDrawId: form.retainer || null,
      };
      if (editing) {
        const body: UpdateTimeEntryRequest = {
          ...common,
          ...(editing.draft === true ? { draft: false } : {}), // completing an open timer
          minutes: form.entryMode === 'duration' ? mins : undefined,
          startAt: form.entryMode === 'startEnd' ? localToIso(form.startAt) : null,
          endAt: form.entryMode === 'startEnd' ? localToIso(form.endAt) : null,
        };
        await api.admin.updateTimeEntry(editing.id, clientId, body);
      } else {
        const body: CreateTimeEntryRequest = {
          ...common,
          clientId,
          minutes: form.entryMode === 'duration' ? mins : undefined,
          startAt: form.entryMode === 'startEnd' ? localToIso(form.startAt) : undefined,
          endAt: form.entryMode === 'startEnd' ? localToIso(form.endAt) : undefined,
        };
        await api.admin.createTimeEntry(body);
      }
      setFormOpen(false);
      setForm(null);
      setEditing(null);
      await refreshOpenTimers();
      await load(clientId, month, year);
    } catch (e) {
      setFormError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  // s50: pause / resume an open timer (the server banks the running segment).
  const togglePause = async (t: TimeEntry): Promise<void> => {
    setError(null);
    try {
      await api.admin.updateTimeEntry(t.id, t.clientId, { paused: isRunning(t) });
      await refreshOpenTimers();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  // s50: stop a running timer (bank the segment via pause) and open the completion form. Switch the
  // page to the timer's client first so the form saves to the right client and the preview matches.
  const stopAndComplete = async (t: TimeEntry): Promise<void> => {
    try {
      const res = isRunning(t)
        ? await api.admin.updateTimeEntry(t.id, t.clientId, { paused: true })
        : { entry: t };
      if (t.clientId !== clientId) setClientId(t.clientId);
      await refreshOpenTimers();
      openEdit(res.entry);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  // s50: edit an open timer's details (switch to its client first).
  const editTimer = (t: TimeEntry): void => {
    if (t.clientId !== clientId) setClientId(t.clientId);
    openEdit(t);
  };

  const doDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.admin.deleteTimeEntry(deleteTarget.id, deleteTarget.clientId);
      setDeleteTarget(null);
      await refreshOpenTimers();
      if (clientId) await load(clientId, month, year);
    } catch (e) {
      setError(apiErrorMessage(e));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const selectedClient = clients.find((c) => c.id === clientId) ?? null;
  const inc = data?.defaults.incrementMinutes ?? 30;
  const categories = useMemo(() => (data ? categoryOptions(data.retainers) : []), [data]);

  // Live charge preview mirroring the server math (the server is authoritative on save).
  const preview = useMemo(() => {
    if (!form || !data) return null;
    const mins = formMinutes(form);
    if (!mins) return null;
    const rounded = roundUp(mins, inc);
    const hours = rounded / 60;
    if (!form.billable) return { rounded, hours, text: 'Non-billable: no charge, no retainer draw.' };
    const rate = parseFloat(form.hourlyRate);
    const target = data.retainers.find((r) => r.clientServiceId === form.retainer) ?? null;
    if (target) {
      // An edit excludes its own stored draw from the month before recomputing (Q41 self-reverse).
      const priorOwnDraw = editing && editing.retainerDrawId === target.clientServiceId ? (editing.retainerHoursDrawn ?? 0) : 0;
      const remaining = Math.max(0, target.remainingInMonth + priorOwnDraw);
      const drawn = Math.min(hours, remaining);
      const overage = Math.round((hours - drawn) * 100) / 100;
      const charge = Math.round(overage * target.discountedOverageRate * 100) / 100;
      return {
        rounded,
        hours,
        text:
          overage > 0
            ? `${fmtHours(drawn)}h from the retainer, ${fmtHours(overage)}h overage at ${fmtMoney(target.discountedOverageRate)}/hr = ${fmtMoney(charge)}`
            : `${fmtHours(drawn)}h from the retainer, charge $0.00`,
      };
    }
    if (!Number.isFinite(rate) || rate < 0) return { rounded, hours, text: '' };
    return { rounded, hours, text: `Charge: ${fmtMoney(Math.round(hours * rate * 100) / 100)} (${fmtHours(hours)}h at ${fmtMoney(rate)}/hr)` };
  }, [form, data, inc, editing]);

  const retainerName = (id: string | null): string | null => {
    if (!id || !data) return null;
    return data.retainers.find((r) => r.clientServiceId === id)?.serviceName ?? 'Retainer';
  };

  const inputCls = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Time</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Log billable and non-billable time per client. The work category draws retainer hours down
            automatically; mileage arrives in a later slice.
          </p>
        </div>
        <div className="flex w-full flex-wrap items-end gap-3 md:w-auto">
          <label className="block min-w-0 flex-1 md:flex-none">
            <span className="mb-1.5 block text-sm font-medium">Client</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={`${inputCls} md:min-w-[220px]`}>
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                  {c.isInternal ? ' (internal)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">Month</span>
            <MonthPicker value={month} onChange={onPickMonth} className={`${inputCls} text-left`} />
          </label>
          <Button onClick={openCreate} disabled={!data}>
            Log time
          </Button>
        </div>
      </header>

      <QuickTimer
        mode="time"
        selectedClientId={clientId || undefined}
        onChanged={() => {
          void refreshOpenTimers();
          if (clientId) void load(clientId, month, year);
        }}
        onCompleteRequested={(entry) => {
          if (entry.clientId !== clientId) setClientId(entry.clientId);
          openEdit(entry);
        }}
      />

      {(clientId ? openTimers.filter((t) => t.clientId === clientId) : openTimers).length > 0 ? (
        <Card>
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-display text-xl">Running timers</h2>
            <p className="text-xs text-[var(--color-muted)]">{clientId ? 'This client' : 'All clients'}</p>
          </div>
          <div className="mt-3 space-y-3">
            {(clientId ? openTimers.filter((t) => t.clientId === clientId) : openTimers).map((t) => {
              const running = isRunning(t);
              const secs = timerElapsedSeconds(t, timerNow);
              const cName = clients.find((c) => c.id === t.clientId)?.displayName ?? t.clientId;
              return (
                <div key={t.id} className="flex flex-col gap-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`h-2 w-2 rounded-full ${running ? 'animate-pulse bg-green-500' : 'bg-amber-500'}`} aria-hidden="true" />
                    <span className="font-medium">{cName}</span>
                    <span className="text-[var(--color-muted)]">
                      {running ? 'running' : 'paused'} · {Math.floor(secs / 3600)}h {Math.floor((secs % 3600) / 60)}m
                      {t.description ? ` · ${t.description}` : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <PillButton tone={running ? 'amber' : 'green'} onClick={() => void togglePause(t)}>
                      {running ? 'Pause' : 'Resume'}
                    </PillButton>
                    <PillButton tone="green" onClick={() => void stopAndComplete(t)}>
                      Stop and complete
                    </PillButton>
                    <PillButton onClick={() => editTimer(t)}>Edit</PillButton>
                    <PillButton tone="red" onClick={() => setDeleteTarget(t)}>Delete</PillButton>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      {error ? <p className="text-sm text-[var(--tint-red-text)]">Error: {error}</p> : null}
      {!clientId ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">Pick a client to view and log time.</p>
        </Card>
      ) : null}
      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}

      {data && clientId ? (
        <>
          {/* s54: compact totals — one retainer-card width; MTD left-aligned, YTD right-aligned. */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <div className="flex gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">MTD</p>
                  <p className="mt-1 font-display text-xl">
                    {fmtHours(monthStats.mtd.totalHours)}
                    <span className="text-sm text-[var(--color-muted)]"> h</span>
                  </p>
                  <p className="text-sm">{fmtMoney(monthStats.mtd.totalCharge)}</p>
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    {fmtHours(monthStats.mtd.billableHours)}h billable · {fmtHours(monthStats.mtd.nonBillableHours)}h non-billable
                  </p>
                </div>
                <div className="min-w-0 flex-1 text-right">
                  <p className="font-medium">YTD</p>
                  <p className="mt-1 font-display text-xl">
                    {fmtHours(monthStats.ytd.totalHours)}
                    <span className="text-sm text-[var(--color-muted)]"> h</span>
                  </p>
                  <p className="text-sm">{fmtMoney(monthStats.ytd.totalCharge)}</p>
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    {fmtHours(monthStats.ytd.billableHours)}h billable · {fmtHours(monthStats.ytd.nonBillableHours)}h non-billable
                  </p>
                </div>
              </div>
            </Card>

            {data.retainers.map((r) => {
              const pctLeft = r.includedHoursPerMonth > 0 ? (r.remainingInMonth / r.includedHoursPerMonth) * 100 : 0;
              const overage = monthStats.overageByRetainer[r.clientServiceId];
              return (
                <Card key={r.clientServiceId}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{r.serviceName}</p>
                      <p className="text-xs text-[var(--color-muted)]">Retainer, {monthLabel(r.month)}</p>
                    </div>
                    <InfoTip text="Remaining hours are derived from the entries dated in this month; each calendar month starts fresh with the full allotment. Time in a matching work category draws these hours first; once they are used, additional time bills at the discounted overage rate." />
                  </div>
                  <p className="mt-2 font-display text-2xl">
                    {fmtHours(r.remainingInMonth)}
                    <span className="text-sm text-[var(--color-muted)]"> of {fmtHours(r.includedHoursPerMonth)}h left</span>
                  </p>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-[rgb(var(--edge-rgb)/0.10)]">
                    <div
                      className={`h-full rounded-full ${pctLeft <= 0 ? 'bg-red-500' : pctLeft <= 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.max(0, Math.min(100, pctLeft))}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-[var(--color-muted)]">
                    {fmtHours(r.drawnInMonth)}h used. Overage bills at {fmtMoney(r.discountedOverageRate)}/hr.
                    {r.workTypeTags.length ? ` Categories: ${r.workTypeTags.join(', ')}.` : ''}
                  </p>
                  {overage && overage.hours > 0 ? (
                    <p className="mt-1 text-xs font-medium text-[var(--tint-warn-text)]">
                      Overage this month: {fmtHours(overage.hours)}h · {fmtMoney(overage.charge)}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-[var(--color-muted)]">No overage this month.</p>
                  )}
                </Card>
              );
            })}
          </div>
          {data.retainers.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">
              {selectedClient?.displayName ?? 'This client'} has no active retainer; billable time charges the entry's hourly rate.
            </p>
          ) : null}

          {formOpen && form ? (
            <Card>
              <h2 className="font-display text-xl">
                {editing?.draft === true ? 'Complete timer entry' : editing ? 'Edit entry' : 'Log time'}
              </h2>
              {editing?.draft === true ? (
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  This entry is an open timer. Complete it to compute the charge and retainer draw, or save it
                  as a draft to keep it running.
                </p>
              ) : null}
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Date" required>
                  <input type="date" value={form.date} onChange={(e) => update({ date: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Entry mode" required>
                  <select value={form.entryMode} onChange={(e) => update({ entryMode: e.target.value as EntryMode })} className={inputCls}>
                    <option value="duration">Total time worked</option>
                    <option value="startEnd">Start and end time</option>
                  </select>
                </Field>
                {form.entryMode === 'duration' ? (
                  <Field label="Duration" required>
                    <div className="flex items-center gap-2">
                      <input type="number" min={0} value={form.hours} onChange={(e) => update({ hours: e.target.value })} className="w-20 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-3 py-2 text-sm" />
                      <span className="text-sm text-[var(--color-muted)]">h</span>
                      <input type="number" min={0} max={59} value={form.minutes} onChange={(e) => update({ minutes: e.target.value })} className="w-20 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-3 py-2 text-sm" />
                      <span className="text-sm text-[var(--color-muted)]">m</span>
                    </div>
                  </Field>
                ) : (
                  <Field label="Start / end" required>
                    <div className="flex flex-wrap items-center gap-2">
                      <input type="datetime-local" value={form.startAt} onChange={(e) => update({ startAt: e.target.value })} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-3 py-2 text-sm" />
                      <span className="text-sm text-[var(--color-muted)]">to</span>
                      <input type="datetime-local" value={form.endAt} onChange={(e) => update({ endAt: e.target.value })} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-3 py-2 text-sm" />
                    </div>
                  </Field>
                )}
                <Field label="Description" required>
                  <input value={form.description} onChange={(e) => update({ description: e.target.value })} placeholder="On-site printer troubleshooting" className={inputCls} />
                </Field>
                {categories.length > 0 ? (
                  <Field
                    label="Category"
                    tip="What kind of work this was. Categories come from this client's retainer plans; time in a matching category draws those retainer hours first. Pick Other for work outside any retainer."
                  >
                    <select value={form.category} onChange={(e) => onCategoryPicked(e.target.value)} className={inputCls}>
                      <option value="">Other</option>
                      {categories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                <Field label="Hourly rate ($/hr)" required={form.billable}>
                  <input type="number" min={0} step="0.01" value={form.hourlyRate} onChange={(e) => update({ hourlyRate: e.target.value })} className={inputCls} />
                  {data.defaults.hourlyRatePrefill != null ? (
                    <p className="mt-1 text-xs text-[var(--color-muted)]">
                      Default {fmtMoney(data.defaults.hourlyRatePrefill)}/hr. While a retainer draw applies, overage bills at the retainer's rate instead.
                    </p>
                  ) : null}
                </Field>
                <Field
                  label="Linked service (optional)"
                  tip="Reporting context only: ties this entry to a catalog service so reports can group time by service. It does not affect the charge or which retainer the time draws from."
                >
                  <select value={form.serviceId} onChange={(e) => update({ serviceId: e.target.value })} className={inputCls}>
                    <option value="">None</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Project (optional)"
                  tip="Ties this entry to one of the client's projects so the project's totals and (Stage 4) 'Generate Invoice from Project' pick it up. Only active projects accept new entries."
                >
                  <select value={form.projectId} onChange={(e) => update({ projectId: e.target.value })} className={inputCls}>
                    <option value="">None</option>
                    {/* Active projects only take new entries (the PSA norm); a non-active project
                        already linked to the entry being edited stays selectable (grandfathered). */}
                    {projects
                      .filter((p) => p.status === 'active' || p.id === form.projectId)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.status !== 'active' ? ` (${p.status === 'on-hold' ? 'on hold' : p.status})` : ''}
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label="Billable" required>
                  <select value={form.billable ? 'yes' : 'no'} onChange={(e) => update({ billable: e.target.value === 'yes' })} className={inputCls}>
                    <option value="yes">Billable</option>
                    <option value="no">Non-billable (tracked, never invoiced, never draws a retainer)</option>
                  </select>
                </Field>
                {data.retainers.length > 0 && form.billable ? (
                  <Field
                    label="Retainer"
                    tip="Which retainer this time draws hours from. Set automatically from the work category; change it here to override for this entry, or pick No retainer to bill at the hourly rate."
                  >
                    <select value={form.retainer} onChange={(e) => update({ retainer: e.target.value })} className={inputCls}>
                      <option value="">No retainer (bill at the hourly rate)</option>
                      {data.retainers.map((r) => (
                        <option key={r.clientServiceId} value={r.clientServiceId}>
                          {r.serviceName} ({fmtHours(r.remainingInMonth)}h left this month)
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                <Field
                  label="Cash cost (optional)"
                  tip="Real money you paid out for this specific entry, like a subcontractor's fee. Usually 0 for your own time. This is separate from the notional labor rate in Settings, which is your internal 'my time is worth $X/hour' lens and is never billed."
                >
                  <input type="number" min={0} step="0.01" value={form.cost} onChange={(e) => update({ cost: e.target.value })} placeholder="0" className={inputCls} />
                </Field>
              </div>
              {preview ? (
                <p className="mt-3 text-sm">
                  Bills as <strong>{fmtHours(preview.hours)}h</strong> ({preview.rounded} min at the {inc}-minute increment).{' '}
                  {preview.text}
                </p>
              ) : null}
              {formError ? <p className="mt-3 text-sm text-[var(--tint-red-text)]">{formError}</p> : null}
              <div className="mt-4 flex items-center gap-3">
                <Button onClick={() => void doSave()} loading={saving} disabled={saving}>
                  {editing?.draft === true ? 'Complete entry' : editing ? 'Save changes' : 'Log time'}
                </Button>
                {editing?.draft === true ? (
                  <Button variant="secondary" onClick={() => void doSaveDraft()} disabled={saving}>
                    Save draft
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFormOpen(false);
                    setForm(null);
                    setEditing(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          ) : null}

          {monthGroups.length === 0 ? (
            <Card>
              <p className="text-sm text-[var(--color-muted)]">No time logged in {year}.</p>
            </Card>
          ) : (
            monthGroups.map((g) => (
              <div
                key={g.month}
                ref={(el) => {
                  monthRefs.current[g.month] = el;
                }}
                className="scroll-mt-4"
              >
                <Card>
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="font-display text-xl">{monthLabel(g.month)}</h2>
                    <p className="text-xs text-[var(--color-muted)]">
                      Rounded up to {inc}-minute increments
                      {data.defaults.incrementSource === 'client-override' ? ' (client override)' : ''}
                    </p>
                  </div>

                  {/* md+: the table */}
                  <div className="mt-3 hidden overflow-x-auto md:block">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                          <th className="py-2 pr-3">Date</th>
                          <th className="py-2 pr-3">Description</th>
                          <th className="py-2 pr-3">Category</th>
                          <th className="py-2 pr-3">Time</th>
                          <th className="py-2 pr-3">Billable</th>
                          <th className="py-2 pr-3">Retainer draw</th>
                          <th className="py-2 pr-3">Charge</th>
                          <th className="py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {g.entries.map((e) => (
                          <tr key={e.id} id={`src-${e.id}`} className={`border-t border-[rgb(var(--edge-rgb)/0.05)] align-top scroll-mt-24 ${focusId === e.id ? 'bg-[var(--color-primary)]/10' : ''}`}>
                            <td className="whitespace-nowrap py-2 pr-3">{displayDate(e.date)}</td>
                            <td className="py-2 pr-3">
                              {e.description}
                              {e.serviceId ? (
                                <span className="block text-xs text-[var(--color-muted)]">
                                  {services.find((s) => s.id === e.serviceId)?.name ?? e.serviceId}
                                </span>
                              ) : null}
                              {e.projectId ? (
                                <span className="block text-xs text-[var(--color-muted)]">
                                  Project: {projects.find((p) => p.id === e.projectId)?.name ?? e.projectId}
                                </span>
                              ) : null}
                            </td>
                            <td className="py-2 pr-3">{e.category ?? 'Other'}</td>
                            <td className="whitespace-nowrap py-2 pr-3">
                              {Math.floor(e.minutes / 60)}h {e.minutes % 60}m
                            </td>
                            <td className="py-2 pr-3">{e.billable ? 'Yes' : 'No'}</td>
                            <td className="py-2 pr-3">
                              {e.retainerDrawId ? `${fmtHours(e.retainerHoursDrawn ?? 0)}h (${retainerName(e.retainerDrawId)})` : '–'}
                            </td>
                            <td className="whitespace-nowrap py-2 pr-3">
                              {fmtMoney(e.charge)}
                              {e.billed ? <span className="ml-1 rounded bg-[rgb(var(--edge-rgb)/0.10)] px-1.5 py-0.5 text-[10px] uppercase">billed</span> : null}
                            </td>
                            <td className="whitespace-nowrap py-2 text-right">
                              <PillButton disabled={e.billed} onClick={() => openEdit(e)}>
                                Edit
                              </PillButton>
                              <PillButton tone="red" className="ml-2" disabled={e.billed} onClick={() => setDeleteTarget(e)}>
                                Delete
                              </PillButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* under md: stacked cards (mobile-first — the road-side view) */}
                  <div className="mt-3 space-y-3 md:hidden">
                    {g.entries.map((e) => (
                      <div key={e.id} id={`src-m-${e.id}`} className={`rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3 scroll-mt-24 ${focusId === e.id ? 'ring-2 ring-[var(--color-primary)]' : ''}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium">{displayDate(e.date)}</p>
                          <p className="whitespace-nowrap text-sm">
                            {fmtMoney(e.charge)}
                            {e.billed ? <span className="ml-1 rounded bg-[rgb(var(--edge-rgb)/0.10)] px-1.5 py-0.5 text-[10px] uppercase">billed</span> : null}
                          </p>
                        </div>
                        <p className="mt-1 text-sm">{e.description}</p>
                        <p className="mt-1 text-xs text-[var(--color-muted)]">
                          {e.category ?? 'Other'} · {Math.floor(e.minutes / 60)}h {e.minutes % 60}m · {e.billable ? 'Billable' : 'Non-billable'}
                          {e.retainerDrawId ? ` · ${fmtHours(e.retainerHoursDrawn ?? 0)}h from ${retainerName(e.retainerDrawId)}` : ''}
                        </p>
                        <div className="mt-2 flex gap-2">
                          <PillButton disabled={e.billed} onClick={() => openEdit(e)}>
                            Edit
                          </PillButton>
                          <PillButton tone="red" disabled={e.billed} onClick={() => setDeleteTarget(e)}>
                            Delete
                          </PillButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            ))
          )}
        </>
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete this time entry?"
        confirmLabel="Delete entry"
        busy={deleting}
        onConfirm={() => void doDelete()}
        onCancel={() => setDeleteTarget(null)}
      >
        <p>
          This removes the entry{deleteTarget?.retainerDrawId ? ' and returns its drawn hours to the retainer month' : ''}.
          The audit log keeps a snapshot.
        </p>
      </ConfirmDialog>
    </div>
  );
}
